import dayjs from 'dayjs';
import { useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { CheckoutContext, CheckoutContextProps } from '../context/CheckoutContext';

import { getDateInTimezone, getFormattedTime, getTimezone } from '../Utils/format';
import { Addon } from './useAddons';
import { AvailabilityDuration, AvailabilitySlot } from './useAvailableStartingTimes';
import axiosInstance from '../services/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { Session } from './useClassSessions';
import { getCountdownTime } from '../Utils/validate';
import { addCartItemsEvent } from '../Utils/gtmEvents';
import { localizeMessage } from '../Utils/dfcMessages';

export interface Booking {
  id: string;
  cartId: string;
  startTime: string;
  endTime: string;
  price: number;
  date: string;
  service: {
    serviceName: string;
    currency?: string;
    servicePrice?: number;
  };
  resource?: {
    resourceName: string;
  };
  serviceDuration: {
    durationTime: number;
    currency: string;
  };
  timeMultipleOf?: number;
  quantity: number;
  addonsPurchases?: AddonPurchase[];
  newCartTotal?: number;
  customer?: {
    email: string;
    name: string;
  };
  createdByAdmin?: boolean;
  paymentStatus?: string;
  marketplaceId?: string;
  company?: {
    id: string;
    companyName: string;
    logo: string;
    commissionPercentage?: number;
  }
  paidAmount?: number;
  serviceId?: string;
  status?: string;
  ticketScanned?: boolean;
  ticketScannedAt?: string;
  schoolInformation?: {
    schoolId?: string;
    schoolName?: string;
    ageGroup?: string;
    numberOfStudents?: number;
    hasAdditionalNeeds?: boolean;
    additionalNeedsInfo?: string;
    bookingContact?: string;
    contactMobile?: string;
    schoolEmirate?: string;
    teacherChaperoneCount?: number;
    chaperoneCount?: number;
    teacherFirstName?: string;
    teacherLastName?: string;
    teacherTelephone?: string;
    additionalInfo?: string;
    cancellationAcknowledged?: boolean;
    termsAgreed?: boolean;
    marketingConsent?: boolean;
    filmingConsent?: boolean;
    processingConsent?: boolean;
    waiverStoragePath?: string;
    waiverFileName?: string;
    waiverUrl?: string;
  };
}

export interface AddonPurchase {
  paidAmount: number;
  id: string;
  linkedBookingId: string;
  cartId: string;
  addon: Addon;
  purchaseType: string;
  quantity: number;
}

interface AddToCartInput {
  selectedService: string;
  selectedResource?: string;
  selectedDuration?: AvailabilityDuration;
  selectedSlot?: AvailabilitySlot;
  selectedSession?: Session;
  quantity: number;
  addonsList?: { id: string; quantity: number }[];
  childrenInformation?: { name: string; age: number }[];
  schoolInformation?: {
    schoolId?: string;
    schoolName: string;
    ageGroup: string;
    hasAdditionalNeeds: boolean;
    additionalNeedsInfo?: string;
    bookingContact: string;
    contactMobile: string;
    additionalInfo?: string;
    cancellationAcknowledged?: boolean;
    termsAgreed?: boolean;
    marketingConsent?: boolean;
    filmingConsent?: boolean;
    processingConsent?: boolean;
    numberOfStudents: number;
    schoolEmirate?: string;
    teacherChaperoneCount?: number;
    chaperoneCount?: number;
    teacherFirstName?: string;
    teacherLastName?: string;
    teacherTelephone?: string;
    waiverStoragePath?: string;
    waiverFileName?: string;
    waiverUrl?: string;
  };
  timezone?: string;
  accessCode?: string;
  silent?: boolean;
  /** Called with backend failure details (e.g. code SLOT_FULL) when the booking cannot be added. */
  onError?: (details: { code?: string; message?: string }) => void;
}

export interface CustomerInformation {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  token?: string;
  issuer?: string;
  /** Set by the terms card from its visible ENBD toggle at the moment of checkout */
  hasNbdDiscount?: boolean;
  cardBin?: string;
}

const getStartTime = (slot: AvailabilitySlot, timezone?: string) => {
  return getFormattedTime(slot.fullDate, 'HHmm', false, getTimezone(timezone));
};

const getEndTime = (
  slot: AvailabilitySlot,
  selectedDuration: AvailabilityDuration,
  timezone?: string
) => {
  return getDateInTimezone(slot.fullDate, false, getTimezone(timezone))
    .add(selectedDuration.durationTime, 'minutes')
    .format('HHmm');
};

const useCart = (syncCart?: boolean) => {
  const { user } = useAuth();
  const {
    bookingCart,
    setBookingCart,
    countdownApi,
    setIsCartLoading,
    setIsCheckoutProcessing,
    company,
    
    splitPayment,
    numOfParticipants,
  } = useContext(CheckoutContext) as CheckoutContextProps;

  const getCart = useCallback(async (id: string) => {
    setIsCartLoading(true);
    try {
      const { data: response } = await axiosInstance.get(`/cart/${id}`);
      setIsCartLoading(false);
      return response.data;
    } catch (error) {
      setIsCartLoading(false);
      throw error;
    }
  }, [setIsCartLoading]);

  const refreshCart = useCallback(async (forceRefresh?: boolean) => {
    if (localStorage.getItem('cart') && (!bookingCart?.length || forceRefresh)) {
      const { id, time } = JSON.parse(localStorage.getItem('cart') || "{id: 0, time: ''}");

      // Get cart
      const data = await getCart(id);

      const bookingsCart: Booking[] = data?.bookings?.map((booking: Booking) => {
        return {
          ...booking,
          addonsPurchases: data.addonPurchases?.filter(
            (addon: AddonPurchase) => addon.linkedBookingId === booking.id
          ),
        };
      }) || [];

      // Get remaining time
      if (time) {
        const timeRemaining = Math.floor(
          (new Date(time).getTime() - new Date().getTime()) / 1000
        );
        if (timeRemaining > 0 && countdownApi) {
          countdownApi.restart(
            new Date(new Date().setSeconds(new Date().getSeconds() + timeRemaining))
          );

          setBookingCart(bookingsCart);
        } else {
          localStorage.removeItem('cart');
        }
      }
    }
  }, [bookingCart, countdownApi, setBookingCart, getCart]);
  
  useEffect(() => {
    if (syncCart) refreshCart(false);
  }, [syncCart, refreshCart]);

  const addToCart = async ({
    selectedDuration,
    selectedService,
    selectedSlot,
    selectedSession,
    quantity,
    addonsList,
    childrenInformation = [],
    schoolInformation,
    selectedResource,
    timezone,
    accessCode,
    silent,
    onError,
  }: AddToCartInput) => {
    setIsCartLoading(true);
    try {
      const { data: response } = await axiosInstance.post(`/cart/bookings`, {
        serviceId: selectedService,
        serviceDurationId: selectedDuration?.id,
        date: selectedSlot ? dayjs(selectedSlot.fullDate).format('DDMMYYYY') : '',
        startTime: selectedSlot ? getStartTime(selectedSlot, timezone) : '',
        endTime:
          selectedSlot && selectedDuration
            ? getEndTime(selectedSlot, selectedDuration, timezone)
            : '',
        quantity,
        cartId: bookingCart.length > 0 ? bookingCart[0].cartId : '',
        sessionId: selectedSession?.id,
        addonsList,
        childrenInformation,
        schoolInformation,
        marketplaceId : company?.isMarketplace ? company?.id : undefined,
        resourceId: selectedResource,
        accessCode,
      });
      if(response.error === 0  && accessCode) {
        setBookingCart(cart => cart.concat(response.data));
        setIsCartLoading(false);
        return response.data;
      } else if (response.error === 0) {
        const currentTime = new Date();
        const newCountdown = new Date(
          currentTime.setSeconds(currentTime.getSeconds() + getCountdownTime(company!))
        );

        if (bookingCart.length === 0) {
          countdownApi.restart(newCountdown);
          localStorage.setItem(
            'cart',
            JSON.stringify({
              id: response.data.booking.cartId,
              time: newCountdown,
            })
          );
        }
        const newBooking: Booking = response.data.booking;
        newBooking.addonsPurchases = response.data.addonPurchases;
        addCartItemsEvent('add_to_cart', bookingCart.concat(newBooking));
        setBookingCart(cart => cart.concat(newBooking));
        if (!silent) {
          toast(localizeMessage('Booking slot is added to the cart'), {
            theme: 'colored',
            type: 'success',
          });
        }
        setIsCartLoading(false);
        return newBooking;
      } else {
        setIsCartLoading(false);
        onError?.({ code: response.code, message: response.message });
        return false;
      }
    } catch (error: any) {
      setIsCartLoading(false);
      onError?.({
        code: error?.response?.data?.code,
        message: error?.response?.data?.message,
      });
      return false;
    }
  };

  const removeFromCart = async (booking: Booking) => {
    setIsCartLoading(true);
    try {
      await axiosInstance.delete(`/cart/bookings`, {
        data: {
          bookingId: booking.id,
          cartId: booking.cartId,
        },
      });

      toast(localizeMessage('Booking slot is removed from the cart'), {
        theme: 'colored',
        type: 'error',
      });

      const newBookingCart = bookingCart.filter(_booking => _booking.id !== booking.id);
      setBookingCart(newBookingCart);

      if (newBookingCart.length === 0) {
        countdownApi.pause();
        localStorage.removeItem('cart');
      }
      setIsCartLoading(false);
    } catch (error) {
      setIsCartLoading(false);
    }
  };

  const removeAddonFromCart = async (addonPurchase: AddonPurchase) => {
    setIsCartLoading(true);
    try {
      await axiosInstance.delete(`/cart/addon-purchase`, {
        data: {
          addonPurchaseId: addonPurchase.id,
          cartId: addonPurchase.cartId,
        },
      });

      toast(localizeMessage('Addon is removed from the cart'), {
        theme: 'colored',
        type: 'error',
      });

      const newBookingCart = bookingCart.filter(
        _booking => _booking.id !== addonPurchase.linkedBookingId
      );

      const linkedBooking = bookingCart.find(
        booking => booking.id === addonPurchase.linkedBookingId
      )!;

      const newAddonPurchases = linkedBooking?.addonsPurchases?.filter(
        _addonPurchase => _addonPurchase.id !== addonPurchase.id
      );

      linkedBooking.addonsPurchases = newAddonPurchases;

      newBookingCart.push(linkedBooking);

      setBookingCart(newBookingCart);
      setIsCartLoading(false);
    } catch (error) {
      setIsCartLoading(false);
    }
  };

  const updateAddonInCart = async (addonPurchase: AddonPurchase, newQuantity: number) => {
    setIsCartLoading(true);
    try {
      await axiosInstance.patch(`/cart/addon-purchase`, {
        addonPurchaseId: addonPurchase.id,
        cartId: addonPurchase.cartId,
        quantity: newQuantity,
      });

      const newBookingCart = bookingCart.filter(
        _booking => _booking.id !== addonPurchase.linkedBookingId
      );

      const linkedBooking = bookingCart.find(
        booking => booking.id === addonPurchase.linkedBookingId
      )!;

      const newAddonPurchases =
        linkedBooking?.addonsPurchases?.filter(
          _addonPurchase => _addonPurchase.id !== addonPurchase.id
        ) || [];

      addonPurchase.quantity = newQuantity;
      linkedBooking.addonsPurchases = [...newAddonPurchases, addonPurchase];

      newBookingCart.push(linkedBooking);

      setBookingCart([...newBookingCart]);
      setIsCartLoading(false);
    } catch (error) {
      setIsCartLoading(false);
    }
  };

  const checkoutCart = async (
    packageCode?: string,
    customerInformation?: CustomerInformation,
    useCheckoutLink: boolean = true,
    cartIdOverride?: string
  ) => {
    setIsCheckoutProcessing(true);
    const cartId = cartIdOverride || (bookingCart.length > 0 ? bookingCart[0].cartId : '');
    const params = {
      cartId,
      voucherCode: packageCode || '',
      splitPayment,
      numOfParticipants,
      ...customerInformation,
      issuer: customerInformation?.issuer || 'N/A',
      useCheckoutLink,
      // Only the terms card's visible ENBD toggle turns the discount on (it may also pick the payment gateway);
      // flows without it, such as school bookings, never send it
      hasNbdDiscount: Boolean(customerInformation?.hasNbdDiscount),
    };
    try {
      const { data: response } = await axiosInstance.post(`/cart/checkout`, params);

      if (response.data.paymentUrl === 'false' || !response.data.paymentUrl) {
        setBookingCart([]);
        countdownApi.pause();
        localStorage.removeItem('cart');
        const baseUrl = window.location.origin;
        const url = splitPayment
          ? `${baseUrl}/GroupBookingConfirmation?cartId=${cartId}&companyId=${company?.id}&isCustomerPaid=true`
          : `${baseUrl}/Confirmation?companyId=${company?.id}&cartId=${cartId}&customerEmail=${
              user?.email || customerInformation?.email
            }`;
        window.location.replace(url);
      } else {
        localStorage.removeItem('cart');
        window.location.href = response.data.paymentUrl;
      }
    } catch (error: any) {
      setIsCheckoutProcessing(false);
      toast(
        error?.response?.data?.message ||
          localizeMessage('An error has occurred while processing your booking payment'),
        {
          theme: 'colored',
          type: 'error',
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { cardBin, ...loggableParams } = params; // card digits are never logged
      console.error('Cart checkout failed', error?.response?.data?.message, loggableParams);
      throw new Error('Cart checkout failed');
    }
  };

  // useMemo(() => {
  //   const setCart = async () => {
  //     if (localStorage.getItem("cart") && !bookingCart?.length) {
  //       const { id, time } = JSON.parse(
  //         localStorage.getItem("cart") || "{id: 0, time: ''}"
  //       );

  //       // Get cart
  //       const data = await getCart(id);

  //       const bookingsCart: Booking[] = data.bookings.map(
  //         (booking: Booking) => {
  //           return {
  //             ...booking,
  //             addonPurchases: data.addonPurchases.filter(
  //               (addon: AddonPurchase) => addon.linkedBookingId === booking.id
  //             ),
  //           };
  //         }
  //       );

  //       // Get remaining time
  //       if (time) {
  //         const timeRemaining = Math.floor(
  //           (new Date(time).getTime() - new Date().getTime()) / 1000
  //         );
  //         if (timeRemaining > 0) {
  //           countdownApi.restart(
  //             new Date(
  //               new Date().setSeconds(new Date().getSeconds() + timeRemaining)
  //             )
  //           );

  //           setBookingCart(bookingsCart);
  //         } else {
  //           localStorage.removeItem("cart");
  //         }
  //       }
  //     }
  //   };

  //   setCart();
  // }, [countdownApi, setBookingCart]);

  return {
    addToCart,
    checkoutCart,
    removeFromCart,
    removeAddonFromCart,
    updateAddonInCart,
    getCart,
    refreshCart,
  };
};

export default useCart;
